/**
 * FullStacked Cloud Console - Authenticated shell: top bar, primary tabs, routing and access
 * context.
 */

import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import type { UserAccess } from "../../../src/common/types.ts";
import { getUserAccess, logout } from "../api/auth.ts";
import type { ApiContext } from "../api/client.ts";
import type { PublicConfig, SessionPayload } from "../api/types.ts";
import { AppContext, type AppState, type NavigateOptions } from "../app-context.ts";
import { formatRoute, parseRoute, topView, type Route } from "../lib/router.ts";
import { DesktopClientBanner } from "../banner.tsx";
import { checkRestricted, isRestrictedError } from "../lib/restricted.ts";
import { RestrictedAccessPanel } from "../restricted-access.tsx";
import { ErrorState, Loading } from "../ui/atoms.tsx";
import { limitFromError, type LimitContext } from "../lib/limit.ts";
import { ConsoleHeader } from "./header.tsx";
import { NavTabs } from "./nav.tsx";
import { useBilling } from "./use-billing.ts";

const ProjectsView = lazy(() => import("../projects-view.tsx"));
const ProjectDetail = lazy(() => import("../projects/detail.tsx"));
const TunnelsView = lazy(() => import("../tunnels-view.tsx"));
const EdgeView = lazy(() => import("../tunnels/edge-detail.tsx"));
const DataView = lazy(() => import("../data-view.tsx"));
const DataInstanceView = lazy(() => import("../data/instance.tsx"));
const UsersView = lazy(() => import("../users-view.tsx"));
const AuthorityView = lazy(() => import("../auth/authority-detail.tsx"));
const AgentsView = lazy(() => import("../mcp-view.tsx"));
const SettingsView = lazy(() => import("../settings-view.tsx"));
const InvitationsView = lazy(() => import("../invitations-view.tsx"));
const InvitationPage = lazy(() => import("../invitations/invitation-page.tsx"));
const LimitReachedDialog = lazy(() => import("../ui/billing/limit-dialog.tsx"));

function renderRoute(route: Route) {
    switch (route.view) {
        case "projects":
            return <ProjectsView />;
        case "project":
            return (
                <ProjectDetail
                    key={route.id}
                    id={route.id}
                    tab={route.tab}
                    path={route.path}
                    hash={route.hash}
                />
            );
        case "tunnels":
            return <TunnelsView tab={route.tab} />;
        case "edge":
            return <EdgeView key={route.id} id={route.id} tab={route.tab} />;
        case "data":
            return <DataView />;
        case "data-instance":
            return <DataInstanceView key={route.id} id={route.id} />;
        case "users":
            return <UsersView tab={route.tab} />;
        case "authority":
            return <AuthorityView key={route.id} id={route.id} tab={route.tab} />;
        case "agents":
            return <AgentsView tab={route.tab} />;
        case "settings":
            return <SettingsView tab={route.tab} />;
        case "invitations":
            return <InvitationsView />;
        case "invitation":
            return <InvitationPage key={route.id} id={route.id} />;
        case "not-found":
            return <ErrorState error={`No console page at ${route.path}`} />;
    }
}

function currentRoute(): Route {
    return parseRoute(window.location.pathname, window.location.search, window.location.hash);
}

export function ConsoleShell(props: {
    config: PublicConfig;
    session: SessionPayload;
    onSessionEnded: () => void;
}) {
    const { config, session, onSessionEnded } = props;
    const [route, setRoute] = useState<Route>(() => currentRoute());
    const [access, setAccess] = useState<UserAccess | null>(null);
    const [accessError, setAccessError] = useState<string | null>(null);
    const [restricted, setRestricted] = useState(false);
    const [accessTick, setAccessTick] = useState(0);
    const [limit, setLimit] = useState<LimitContext | null>(null);
    const [quotaTick, setQuotaTick] = useState(0);

    const api = useMemo<ApiContext>(
        () => ({
            services: config.services,
            getToken: () => session.token,
            onUnauthorized: onSessionEnded,
            // Any 402 QUOTA_EXCEEDED opens Limit Reached and refreshes the meters.
            onQuotaExceeded: (err) => {
                setLimit((open) => open ?? limitFromError(err));
                setQuotaTick((t) => t + 1);
            },
        }),
        [config, session, onSessionEnded]
    );
    const { billing, reload: reloadBilling } = useBilling(api, access);
    const refreshBilling = useCallback(() => {
        reloadBilling();
        setAccessTick((t) => t + 1);
    }, [reloadBilling]);
    useEffect(() => {
        if (quotaTick) reloadBilling();
    }, [quotaTick, reloadBilling]);

    useEffect(() => {
        const onPop = (): void => setRoute(currentRoute());
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, []);

    useEffect(() => {
        getUserAccess(api)
            .then((a) => {
                setAccess(a);
                setAccessError(null);
                setRestricted(false);
            })
            .catch((err: Error) => {
                if (isRestrictedError(err)) setRestricted(true);
                else setAccessError(err.message);
            });
    }, [api, accessTick]);

    const navigate = useCallback((next: Route, opts: NavigateOptions = {}) => {
        const path = formatRoute(next);
        const { pathname, search, hash } = window.location;
        if (path !== pathname + search + hash) {
            if (opts.replace) window.history.replaceState(null, "", path);
            else window.history.pushState(null, "", path);
        }
        setRoute(next);
        if (!opts.keepScroll) window.scrollTo(0, 0);
    }, []);

    const signOut = useCallback(() => {
        logout(api)
            .catch(() => undefined)
            .finally(onSessionEnded);
    }, [api, onSessionEnded]);

    const state: AppState = {
        api,
        config,
        session,
        access,
        navigate,
        reloadAccess: () => setAccessTick((t) => t + 1),
        billing,
        reloadBilling,
        refreshBilling,
        showLimit: setLimit,
        signOut,
    };

    if (restricted) {
        const user = session.user;
        return (
            <div
                className="console-container"
                style={{ alignItems: "center", justifyContent: "center" }}
            >
                <div className="card" style={{ width: 480, maxWidth: "calc(100% - 32px)" }}>
                    <RestrictedAccessPanel
                        account={user?.email ?? user?.slug ?? null}
                        check={() => checkRestricted(session.token, fetch, config.services.auth)}
                        onUnlocked={state.reloadAccess}
                        onSignOut={signOut}
                    />
                </div>
            </div>
        );
    }

    return (
        <AppContext.Provider value={state}>
            <div className="console-container">
                <ConsoleHeader />
                <NavTabs active={topView(route)} onSelect={navigate} />
                <main className="console-body">
                    <DesktopClientBanner />
                    {accessError ? (
                        <ErrorState
                            error={`Could not load your access profile: ${accessError}`}
                            onRetry={state.reloadAccess}
                        />
                    ) : null}
                    <Suspense fallback={<Loading label="Loading view" />}>
                        {renderRoute(route)}
                    </Suspense>
                    {limit ? (
                        <Suspense fallback={null}>
                            <LimitReachedDialog limit={limit} onClose={() => setLimit(null)} />
                        </Suspense>
                    ) : null}
                </main>
            </div>
        </AppContext.Provider>
    );
}
